import React, { Fragment, useEffect } from 'react';
import { Alert, Button, Card as BSCard, Card, CardBody, CardHeader, Col, Form, Row } from 'reactstrap';

import { assert } from '../base/utils/assert';
import { encURI, modifyHash, isDebug, space } from '../base/utils/miscutils';


import C from '../C';
import DataStore, { getDataPath } from '../base/plumbing/DataStore';
import ActionMan from '../plumbing/ActionMan';
import { getType } from '../base/data/DataClass';
import Misc from '../base/components/Misc';
import PropControl from '../base/components/PropControl';
import BrandingEditor from './BrandingEditor';
import ListItems, { CreateAdThingButton } from './ListItems';
import CloseButton from '../base/components/CloseButton';
import { getDataList, setWindowTitle } from '../base/plumbing/Crud';
import { setNavContext } from '../base/components/NavBar';
import SavePublishDeleteEtc from '../base/components/SavePublishDeleteEtc';
import { MondayLink } from './monday';
import LinkOut from '../base/components/LinkOut';
import ListLoad from '../base/components/ListLoad';
import SearchQuery from '../base/searchquery';
import { ImpactOverviewLink } from './CampaignPage';
import KWorkflow from './KWorkflow';
import { VertiserImpactDebitEditor } from './ImpactSettingsControls';
import KStatus from '../base/data/KStatus';
import {Tabs, Tab} from '../base/components/Tabs';
import Advert from '../base/data/Advert';
import List from '../base/data/List';
import SubCard from '../base/components/SubCard';
import Campaign from '../base/data/Campaign';
import { dateStr, dateUTCfromString} from '../base/utils/date-utils';
import AThing from '../base/data/AThing';
import ImpactDebit from '../base/data/ImpactDebit';
import Advertiser from '../base/data/Advertiser';

function AdvertiserPage() {
	const type = C.TYPES.Advertiser;
	setWindowTitle(type);

	const blocked = Misc.CheckAccess({ can: 'editAdvert' });
	if (blocked) return blocked;

	const path = DataStore.getValue(['location', 'path']);
	const advertiserId = path[1];

	if (!advertiserId) {
		return <>
			<p>
				To create an Advertiser: Request a mock from the monday.com <LinkOut href="https://good-loop.monday.com/boards/2643467896">Deals board</LinkOut>.
			</p>
			<ListItems type={type} canCreate={isDebug()} filterWith={[C.TYPES.Agency]} />
		</>;
	}

	const pvItem = ActionMan.getDataItem({ type, id: advertiserId, status: C.KStatus.DRAFT });
	if (!pvItem.value) {
		return <><h1>Advertiser: {advertiserId}</h1><Misc.Loading /></>;
	}
	const item = pvItem.value;
	setWindowTitle(item);

	return (
		<>
			<h1>Advertiser: {item.name || "unnamed"} <CloseButton size="lg" onClick={() => modifyHash(['advertiser'])} tooltip="Return to the list of advertisers" /></h1>
			<Tabs activeTabId={DataStore.getUrlValue('tab')} setActiveTabId={t => DataStore.setUrlValue('tab', t)}>
				<Tab title="Edit">
					<EditAdvertiser advertiser={item} />
				</Tab>
				<Tab title="Adverts">
					<ListAdvertsForVertiser advertiser={item} />
				</Tab>
				<Tab title="Campaigns">
					<ListCampaignsForVertiser advertiser={item} />
				</Tab>
				<Tab title="Impact Debits">
					<ListImpactsForVertiser advertiser={item} />
				</Tab>
			</Tabs>
		</>
	);
} // ./AdvertiserPage

function ListAdvertsForVertiser({advertiser}) {	
	let pvAds = Advert.fetchForAdvertiser({vertiserId:advertiser.id, status:KStatus.DRAFT});

	return (<BSCard><CardHeader>Advertisers Adverts</CardHeader>
		<CardBody>	
			<div className="gridbox gridbox-md-4">
				{pvAds.value ? List.hits(pvAds.value).sort((a, b) => {
					if(!a.created && !b.created) return 0; // can happen when comparing two unpublished ads
					if(!a.created) return 1;
					if(!b.created) return -1;
					return dateUTCfromString(b.created) - dateUTCfromString(a.created); 
				}).map(ad => {
					return (
					<SubCard key={ad.id} className={space("m-2",ad._hidden && "disabled")}>
						<div className='d-flex flex-row justify-content-between align-items-end'>
							<a href={(ad.format === "display" ? '/#displayad/' : '/#advert/')+encURI(ad.id)}>{ad.name || "unnamed"}</a>
						</div>
						<small>ID: {ad.id}</small>
						{ad.created ? <small>Created: {dateStr(dateUTCfromString(ad.created))}</small> : <small>Created: not published yet</small>}
						{ad.lastModified && <small>Last modified: {dateStr(dateUTCfromString(ad.lastModified))}</small>}
						{ad.serving && <small className='text-success'>Ad currently serving</small>}
						{ad.hasServed && <small className='text-success'>Ad previously served</small>}
						{ad.format === "display" && <code>Display ad</code>}
						{ad.branding && ad.branding.logo && <img src={ad.branding.logo} className='position-absolute w-25' style={{bottom: 0, right:0}}/>}
					</SubCard>
					);
				}
				) : <Misc.Loading />}
			</div>
		</CardBody>
	</BSCard>);
}

function ListCampaignsForVertiser({advertiser}) {
	let pvCampaigns = Campaign.fetchForAdvertiser(advertiser.id);
 
	console.log("pv", pvCampaigns);
	return (<BSCard><CardHeader>Advertisers Campaigns</CardHeader>
		<CardBody>	
			<div className="gridbox gridbox-md-4">
				{pvCampaigns.value ? List.hits(pvCampaigns.value).sort((a, b) => {
					// sort so if vertiser is a masterbrand, sort so direct children of vertiser come first
					if(a.vertiser !== advertiser.id && b.vertiser !== advertiser.id) return 0;
					if(a.vertiser === advertiser.id) return -1;
					if(b.vertiser === advertiser.id) return 1;
				}).map(campaign => {
					return (
					<SubCard key={campaign.id} className={space("m-2",campaign._hidden && "disabled")}>
						<div className='d-flex flex-row justify-content-between align-items-end'>
							<a style={{textWrap: "wrap", width:"100%"}} href={`/#campaign/${encURI(campaign.id)}`}>{campaign.name || "unnamed"}</a>
						</div>
						<p>Status: {campaign.status}</p>
						<small> Created: <Misc.RoughDate date={campaign.created} /></small>
						{AThing.lastModified(campaign) && <small>Modified: {dateStr(dateUTCfromString(campaign.lastModified))}</small>}
						<small>ID: {campaign.id}</small>
						{advertiser.id !== campaign.vertiser && <small className='text-danger'>{advertiser.name} is a parent brand</small>}
						{campaign.branding && campaign.branding.logo && <img src={campaign.branding.logo} className='position-absolute w-25' style={{bottom: 0, right:0}}/>}
					</SubCard>
					);
				}
				) : pvCampaigns.resolved ? <Misc.Loading /> : <h3>No campaigns found</h3>}
			</div>
		</CardBody>
	</BSCard>);
}

function ListImpactsForVertiser({advertiser}) {
	let pvImpacts = Advertiser.getImpactDebits({vertiser:advertiser});
	return (<BSCard><CardHeader>Advertisers Impact Debits</CardHeader>
		<CardBody>	
			<div className="gridbox gridbox-md-4">
				{pvImpacts.value ? List.hits(pvImpacts.value).map(debit => {
					return (
					<SubCard key={debit.id} className={space("m-2",debit._hidden && "disabled")}>
						<div className='d-flex flex-row justify-content-between align-items-end'>
							<a href={`/#campaign/${encURI(debit.id)}`}>{debit.name || "unnamed"}</a>
						</div>
						<p>Job: {debit.jobNumber}</p>
						<small> Created: <Misc.RoughDate date={debit.created} /></small>
						{debit.end && <small> Ended: <Misc.RoughDate date={debit.end} /></small>}
						{AThing.lastModified(debit) && <small>Modified: <Misc.RoughDate date={AThing.lastModified(debit)} /></small>}
						<small>ID: {debit.id}</small>
					</SubCard>
					);
				}
				) : pvImpacts.resolved ? <Misc.Loading /> : <h3>No debits found</h3>}
			</div>
		</CardBody>
	</BSCard>);
}


function EditAdvertiser({ advertiser }) {
	assert(advertiser, 'AdvertiserPage.jsx - no advertiser?! - if new, ListLoad create should take care of this');
	// HACK: patch malformed items
	if (!getType(advertiser)) advertiser['@type'] = C.TYPES.Advertiser;
	if (!advertiser.id) advertiser.id = C.newId;
	assert(advertiser.id, advertiser);

	useEffect(() => {
		setNavContext(C.TYPES.Advertiser, advertiser.id, true);
		setNavContext(C.TYPES.Advert, null);
	}, [advertiser]);

	// <ShareLink item={advertiser} /> removed for now -- add in when access control is wanted
	// <ShareWidget item={advertiser} />

	return <BSCard><CardHeader>Edit Advertiser</CardHeader>
		<CardBody>
		<h3>{advertiser.id === C.newId && 'Register New Advertiser'}</h3>
		{ (advertiser.id !== C.newId) && (
			<div>
				{isDebug() && <><CreateAdThingButton type={C.TYPES.Advert} navpage="advert" createWith={[C.TYPES.Advertiser]}>Create an Advert</CreateAdThingButton>
					- <CreateAdThingButton type={C.TYPES.Advert} navpage="displayad" createWith={[C.TYPES.Advertiser]}>Create a Display (banner) Advert</CreateAdThingButton>
				</>}
			</div>
		)}
		<EditAdvertiserForm advertiser={advertiser} />
		</CardBody>
	</BSCard>;
}


function EditAdvertiserForm({ advertiser }) {
	assert(advertiser.id, advertiser);
	let path = getDataPath({ status: C.KStatus.DRAFT, type: C.TYPES.Advertiser, id: advertiser.id });
	let bpath = path.concat('branding');
	let b = advertiser.branding;

	// TODO <PartnerControlSummary item={advertiser} /> currently done via Adverts
	// 			<CharityControls entity={advertiser} /> done via Adverts
	// NB Brand name has class "brand-name" to make it unambiguously selectable in tests, do not remove
	return (
		<div className="form">
			ID: {advertiser.id} 
			<MondayLink board="2559467794" item={advertiser} path={path} type="Brand" size="sm" />
			<PropControl className="brand-name" label="Brand Name" item={advertiser} path={path} prop="name" />
			<PropControl canCreate prop="parentId" path={path} label="Parent Brand / Advertiser" type="DataItem" itemType={C.TYPES.Advertiser} 
				status={C.KStatus.PUB_OR_DRAFT}
				help="Does this brand have a parent? e.g. General Mills has Nature Valley & Quaker Oats" />
			<h5>Child Brands</h5>
			<ListLoad q={"parentId:"+advertiser.id} canCreate={false} canDelete={false} canFilter={false} type={C.TYPES.Advertiser} status={KStatus.ALL_BAR_TRASH} />

			<ImpactOverviewLink item={advertiser} type={C.TYPES.Advertiser}/>
			<PropControl label="Website" help="The main website for the advertiser - displays on the Impact Hub" path={path} prop="url" type="text" item={advertiser} />
			{/* contact managed in CRM */}
			<PropControl canCreate prop="agencyId" path={path} label="Agency" type="DataItem" itemType={C.TYPES.Agency} status={C.KStatus.PUB_OR_DRAFT} />
			<PropControl label="Notes" path={path} prop="notes" type="textarea" item={advertiser} />
			<hr />
			<p>The controls below are optional - branding is normally set at the Advert level.</p>
			<BrandingEditor entity={advertiser} path={path} workflow={KWorkflow.ImpactHub}>
				<PropControl prop="csrHtml" path={path.concat('impactSettings')} type="textarea" label="Impact Hub - Wider CSR (markdown / html)" />
				<PropControl prop="customCss" path={path.concat('impactSettings')} type="textarea" label="Impact Hub - Custom CSS" />
			</BrandingEditor>
			<h4>Impact Stories</h4>
			<PropControl label="Background Image" type="imgUpload" item={b} prop="backgroundImage" path={bpath} version="standard" cacheControls
					help="Recommended format: .svg (which can scale nicely without pixelation). Otherwise, we recommend 256px square .png or .jpg.  When you drag-and-drop an image file into the appropriate uploader field, your image will be uploaded and preserved in its original state as well as optimised by ImageMagick in an attempt to strip un-needed bytes off of the image without a major quality sacrifice.  This optimised version of your image is what you will see on your rendered adunit."
			/>
			<h5>Stories Testimonials</h5>
			<VertiserImpactDebitEditor vertiser={advertiser} />
			<SavePublishDeleteEtc type={C.TYPES.Advertiser} id={advertiser.id} sendDiff />

		</div>
	);
}

export default AdvertiserPage;
